import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import "./Navbar.css";

export default function Navbar({ setBackgroundBlur }) {
    const [activeNav, setActiveNav] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
    const menuRef = useRef(null);
    const location = useLocation();

    const handleMouseOver = (index) => {
        setActiveNav(index);
        if (typeof setBackgroundBlur === 'function') {
            setBackgroundBlur(true);
        }
    };

    const handleMouseOut = () => {
        setActiveNav(null);
        if (typeof setBackgroundBlur === 'function') {
            setBackgroundBlur(false);
        }
    };

    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setIsMenuOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        // Close menus upon navigation
        setIsMenuOpen(false);
        setActiveNav(null);
        setOpenMobileSubmenu(null);
        if (typeof setBackgroundBlur === 'function') {
            setBackgroundBlur(false);
        }
    }, [location.pathname, location.hash, setBackgroundBlur]);

    const handleLogoClick = (e) => {
        setIsMenuOpen(false);
        setActiveNav(null);
        if (location.pathname === '/') {
            e.preventDefault();
            if (window.location.hash) {
                window.history.pushState(null, '', '/');
            }
            if (window.lenis) {
                window.lenis.scrollTo(0);
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    };

    const navItems = [
        {
            title: "Training",
            link: "/training",
            linkTitle: "Training Overview",
            subItems: [
                { name: "Pilot Training", link: "/training#pilot-training", linkTitle: "Pilot Training" },
                { name: "Degree Programs", link: "/training#degree-programs", linkTitle: "Degree Programs" },
                { name: "Certification Programs", link: "/training#certification-programs", linkTitle: "Certification Programs" },
            ]
        },
        {
            title: "Technology",
            link: "/technology",
            linkTitle: "Technology & 3D Showcase"
        },
        {
            title: "Colleges",
            link: "/colleges",
            linkTitle: "College Partners Overview"
        },
        {
            title: "For Universities",
            link: "/for-universities",
            linkTitle: "For Universities"
        },
        {
            title: "Career",
            link: "/careers",
            linkTitle: "Careers at MH Cockpit"
        },
        {
            title: "Blogs",
            link: "/blogs",
            linkTitle: "Blog Home"
        },
        {
            title: "About Us",
            link: "/about-us",
            linkTitle: "About Us"
        },
        {
            title: "Students",
            link: "/students",
            linkTitle: "Students"
        },
        {
            title: "Login",
            link: "https://mhc.exploremira.com/login/",
            linkTitle: "Login",
            isExternal: true,
            target: "_blank"
        },
    ];

    const isItemActive = (item) => {
        if (!item.link || item.isExternal) return false;
        const currentPath = location.pathname.toLowerCase();
        const linkPath = item.link.split('#')[0].toLowerCase();

        if (linkPath === '/') {
            return currentPath === '/';
        }

        if (currentPath === linkPath || currentPath.startsWith(`${linkPath}/`)) {
            return true;
        }

        // Also check if any subItem matches
        if (item.subItems && item.subItems.length > 0) {
            return item.subItems.some((sub) => {
                if (!sub.link) return false;
                const subPath = sub.link.split('#')[0].toLowerCase();
                return subPath && (currentPath === subPath || currentPath.startsWith(`${subPath}/`));
            });
        }

        return false;
    };

    const isSubItemActive = (subItem) => {
        if (!subItem.link) return false;
        const currentPath = location.pathname.toLowerCase();
        const [subPath, subHash] = subItem.link.toLowerCase().split('#');

        if (subHash) {
            return currentPath === subPath && location.hash.toLowerCase() === `#${subHash}`;
        }
        return currentPath === subPath;
    };

    return (
        <nav className="fixed top-0 left-0 right-0 w-full z-50 bg-black/95 backdrop-blur-md border-b border-white/10 shadow-lg transition-all duration-200">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-12">
                    {/* Left: Brand Logo */}
                    <div className="flex-shrink-0 flex items-center">
                        <Link 
                            to="/" 
                            title="MH Cockpit - Home" 
                            aria-label="MH Cockpit - Go to Homepage" 
                            className="flex items-center group"
                            onClick={handleLogoClick}
                        >
                            <img 
                                className="h-8 sm:h-8.5 w-auto max-w-[150px] sm:max-w-[170px] object-contain" 
                                src="/MH_Cockpit_TM_LOGO_white.png" 
                                alt="MH Cockpit Logo" 
                                title="MH Cockpit Logo" 
                                loading="eager"
                            />
                        </Link>
                    </div>

                    {/* Center: Desktop Navigation Links */}
                    <div className="hidden xl:flex items-center">
                        <ul className="flex items-center gap-6 xl:gap-7 2xl:gap-10">
                            {navItems.map((item, index) => {
                                const hasSubItems = item.subItems && item.subItems.length > 0;
                                const isActive = isItemActive(item);

                                return (
                                    <li
                                        key={index}
                                        className={`heading ${hasSubItems ? 'has-dropdown' : ''}`}
                                        onMouseEnter={() => hasSubItems && handleMouseOver(index)}
                                        onMouseLeave={handleMouseOut}
                                    >
                                        {item.isExternal ? (
                                            <a
                                                href={item.link}
                                                target={item.target || "_blank"}
                                                rel="noopener noreferrer"
                                                className={`nav-link py-1 ${isActive ? 'active' : ''}`}
                                                title={item.linkTitle || item.title}
                                            >
                                                <span className="nav-link-inner">
                                                    {item.title}
                                                </span>
                                            </a>
                                        ) : (
                                            <Link
                                                to={item.link}
                                                className={`nav-link py-1 ${isActive ? 'active' : ''}`}
                                                title={item.linkTitle || item.title}
                                            >
                                                <span className="nav-link-inner">
                                                    {item.title}
                                                    {hasSubItems && (
                                                        <svg
                                                            className={`nav-chevron ${activeNav === index ? 'nav-chevron--open' : ''}`}
                                                            viewBox="0 0 12 12"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            aria-hidden="true"
                                                        >
                                                            <path
                                                                d="M2.5 4.5L6 8L9.5 4.5"
                                                                stroke="currentColor"
                                                                strokeWidth="1.6"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </span>
                                            </Link>
                                        )}

                                        {hasSubItems && (
                                            <ul className={`nav-list ${activeNav === index ? 'expanded' : ''}`}>
                                                {item.subItems.map((subItem, subIndex) => {
                                                    const isSubActive = isSubItemActive(subItem);
                                                    return (
                                                        <li key={subIndex} className={`sub-1 rounded-md overflow-hidden ${isSubActive ? 'active-sub' : ''}`}>
                                                            <Link
                                                                to={subItem.link}
                                                                title={subItem.linkTitle || subItem.name}
                                                                onClick={() => {
                                                                    setActiveNav(null);
                                                                    if (typeof setBackgroundBlur === 'function') {
                                                                        setBackgroundBlur(false);
                                                                    }
                                                                }}
                                                            >
                                                                {subItem.name}
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Right: Contact Action Button */}
                    <div className="hidden xl:flex items-center">
                        <Link 
                            to="/contact" 
                            title="Contact MH Cockpit"
                            className="bg-[#0C8AC4] hover:bg-[#0a75a6] text-white font-semibold rounded-md py-1 px-3 text-[15px] shadow-sm select-none transition-colors"
                        >
                            Contact
                        </Link>
                    </div>

                    {/* Mobile & Tablet Controls */}
                    <div className="flex xl:hidden items-center space-x-2.5">
                        <Link 
                            to="/contact" 
                            className="bg-[#0C8AC4] text-white font-semibold rounded-md py-1 px-2.5 text-xs sm:text-sm tracking-wide shadow-sm select-none"
                        >
                            Contact
                        </Link>
                        <button 
                            onClick={() => setIsMenuOpen(!isMenuOpen)} 
                            aria-label="Toggle Navigation Menu"
                            aria-expanded={isMenuOpen}
                            className="p-2 rounded-lg text-white hover:text-sky-400 hover:bg-white/10 focus:outline-none transition-colors"
                        >
                            {isMenuOpen ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Drawer */}
            {isMenuOpen && (
                <div 
                    ref={menuRef} 
                    className="xl:hidden bg-black/95 backdrop-blur-md border-t border-neutral-800 shadow-2xl max-h-[82vh] overflow-y-auto px-5 py-5 transition-all duration-200 ease-out"
                >
                    <ul className="flex flex-col space-y-1">
                        {navItems.map((item, index) => {
                            const hasSubItems = item.subItems && item.subItems.length > 0;
                            const isSubmenuOpen = openMobileSubmenu === index;
                            const isActive = isItemActive(item);

                            return (
                                <li key={index} className="border-b border-neutral-800 last:border-b-0 pb-1">
                                    <div className="flex items-center justify-between">
                                        {item.isExternal ? (
                                            <a
                                                href={item.link}
                                                target={item.target || "_blank"}
                                                rel="noopener noreferrer"
                                                onClick={() => setIsMenuOpen(false)}
                                                className="text-white font-medium text-base py-2.5 flex-1 hover:text-sky-400 transition-colors"
                                            >
                                                {item.title}
                                            </a>
                                        ) : (
                                            <Link
                                                to={item.link}
                                                onClick={() => !hasSubItems && setIsMenuOpen(false)}
                                                className={`${
                                                    isActive
                                                        ? 'text-white font-bold bg-white/10 border-l-4 border-[#e71e25] pl-3'
                                                        : 'text-white/90 font-medium pl-1'
                                                } text-base py-2.5 flex-1 hover:text-sky-400 transition-all rounded-r-md`}
                                            >
                                                {item.title}
                                            </Link>
                                        )}
                                        {hasSubItems && (
                                            <button
                                                type="button"
                                                onClick={() => setOpenMobileSubmenu(isSubmenuOpen ? null : index)}
                                                aria-label={`Toggle ${item.title} submenu`}
                                                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                                            >
                                                <svg 
                                                    className={`w-4 h-4 transition-transform duration-200 ${isSubmenuOpen ? 'rotate-180' : ''}`} 
                                                    viewBox="0 0 12 12" 
                                                    fill="none"
                                                >
                                                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>

                                    {/* Mobile Submenu Accordion */}
                                    {hasSubItems && isSubmenuOpen && (
                                        <ul className="pl-4 pb-2 space-y-1.5 border-l-2 border-neutral-700 ml-2 mt-1">
                                            {item.subItems.map((subItem, subIndex) => {
                                                const isSubActive = isSubItemActive(subItem);
                                                return (
                                                    <li key={subIndex}>
                                                        <Link
                                                            to={subItem.link}
                                                            onClick={() => setIsMenuOpen(false)}
                                                            className={`block text-[15px] py-1.5 px-2.5 rounded-md transition-colors ${
                                                                isSubActive
                                                                    ? 'text-white font-semibold bg-white/10'
                                                                    : 'text-neutral-300 hover:text-sky-400 hover:bg-white/5'
                                                            }`}
                                                        >
                                                            {subItem.name}
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    <div className="pt-5 mt-3 border-t border-neutral-800">
                        <Link
                            to="/contact"
                            onClick={() => setIsMenuOpen(false)}
                            className="w-full flex items-center justify-center py-2 px-3 rounded-md bg-[#0C8AC4] text-white font-semibold text-center text-sm shadow-sm select-none"
                        >
                            Contact
                        </Link>
                    </div>
                </div>
            )}
        </nav>
    );
}
